import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, FileText, Mail, Phone, Building2, Calendar, Wrench, ExternalLink, Repeat, PauseCircle, PlayCircle, XCircle, Pencil, Save, Trash2, Plus, Search } from "lucide-react";

const STATUS_PILL = {
  draft: { bg: "#FFF3D4", border: "#7A5A00", fg: "#3B2A00", label: "Draft" },
  sent: { bg: "#DFEEF7", border: "#0A3A7A", fg: "#0A3A7A", label: "Sent" },
  // v176 split — see Orders.jsx for the full explainer.
  received: { bg: "#E6F4EA", border: "#1F6F3E", fg: "#1F6F3E", label: "Delivered" },
  actioned: { bg: "#E6E1F5", border: "#4B2E9B", fg: "#4B2E9B", label: "Actioned" },
  cancelled: { bg: "#EEE", border: "#666", fg: "#333", label: "Cancelled" },
  closed: { bg: "#E4E7EB", border: "#3D4A5C", fg: "#3D4A5C", label: "Closed" },
  partially_received: {
    bg: "#FFF3D4",
    border: "#7A5A00",
    fg: "#3B2A00",
    label: "Part received",
  },
};

// Same "Actioned vs Delivered" split as Orders — see STATUS_PILL block
// above for the rationale.
const resolveStatus = (po) => {
  if (!po) return STATUS_PILL.draft;
  if (po.status !== "received") return STATUS_PILL[po.status] || STATUS_PILL.draft;
  const anyPhysical = (po.items || []).some(
    (it) => Number(it.qty_received || 0) > 0,
  );
  return anyPhysical ? STATUS_PILL.received : STATUS_PILL.actioned;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
};
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

/**
 * Read-only PO detail dialog — opened from the Purchase Orders list to
 * inspect what was on a given order after it's been sent/received. Renders
 * supplier snapshot, items, notes, status timeline and (if present) a
 * link to the companion / parent PO for toll-processing chains.
 *
 * Refetches the PO by id so line totals reflect any inline edits made
 * elsewhere (Reports drill-down snapshot fixes, etc.).
 */
// v206 — Tack a forgotten item onto a draft. Limited to the PO's supplier.
const AddLinePicker = ({ supplierId, existingIds, onAdd }) => {
  const [stock, setStock] = useState(null);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [qty, setQty] = useState("1");
  useEffect(() => {
    api.get("/stock").then(({ data }) => setStock(data)).catch(() => setStock([]));
  }, []);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (stock || [])
      .filter((s) => s.supplier_id === supplierId && !existingIds.includes(s.id))
      .filter((s) => !t || `${s.name} ${s.sku || ""}`.toLowerCase().includes(t))
      .slice(0, 8);
  }, [stock, q, supplierId, existingIds]);
  const add = () => {
    if (!pick || !(Number(qty) > 0)) return toast.error("Pick an item and enter qty > 0");
    onAdd(pick, Number(qty));
    setPick(null);
    setQ("");
    setQty("1");
  };
  return (
    <div data-testid="po-add-line-picker" className="p-2.5 border-t-2 border-dashed border-black/30 bg-[#F6FAFD] flex flex-col gap-2">
      <div className="label-eyebrow flex items-center gap-1"><Plus size={11} strokeWidth={2.5} /> Add a line from this supplier</div>
      {pick ? (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-0 font-bold text-sm truncate" data-testid="po-add-line-picked">
            {pick.name} <span className="text-[10px] text-[#607087]">· {money(pick.unit_cost)} / {pick.unit}</span>
          </div>
          <input type="number" step="0.01" min="0" value={qty} onChange={(e) => setQty(e.target.value)}
            data-testid="po-add-line-qty" className="nb-input py-1.5 text-sm text-right font-mono-num w-24" />
          <button data-testid="po-add-line-confirm" onClick={add} className="nb-btn nb-btn-primary px-3 py-1.5 text-xs">Add</button>
          <button data-testid="po-add-line-clear" onClick={() => setPick(null)} className="nb-btn bg-white p-1.5" aria-label="clear"><X size={12} strokeWidth={2.5} /></button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#607087]" />
            <input data-testid="po-add-line-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={stock ? "Search name or SKU…" : "Loading items…"} className="nb-input py-1.5 pl-8 text-sm w-full" />
          </div>
          {stock && matches.length === 0 && (
            <div className="text-xs text-[#607087]" data-testid="po-add-line-empty">No other items linked to this supplier match.</div>
          )}
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
            {matches.map((s) => (
              <button key={s.id} data-testid={`po-add-line-option-${s.id}`} onClick={() => setPick(s)}
                className="text-left nb-card-sm px-2.5 py-2 bg-white hover:bg-[#EAF5FB] flex items-center justify-between gap-2 text-sm">
                <span className="truncate font-bold">{s.name}{s.sku && <span className="text-[10px] text-[#607087] font-mono-num ml-1">SKU {s.sku}</span>}</span>
                <span className="font-mono-num text-xs shrink-0">{money(s.unit_cost)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const PODetailDialog = ({ open, poId, onClose, onOpenLinked, onChanged }) => {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  // v205 — Inline edit of a Draft PO's line qty / unit_cost / remove.
  // Managers kept missing qty slips because the only way to amend was
  // delete + rebuild. `editItems` is a snapshot of po.items the user is
  // currently tweaking; `editing` toggles the mode. Backend already
  // supports PUT /po/{pid} with an `items` payload — just surfacing it.
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editBusy, setEditBusy] = useState(false);

  const reload = () => {
    if (!poId) return;
    setLoading(true);
    api
      .get(`/po/${poId}`)
      .then(({ data }) => setPo(data))
      .catch(() => toast.error("Could not load PO"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !poId) {
      setPo(null);
      setEditing(false);
      setEditItems([]);
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, poId]);

  const startEdit = () => {
    setEditItems(
      (po.items || []).map((it) => ({
        ...it,
        qty: Number(it.qty || 0),
        unit_cost: Number(it.unit_cost || 0),
      })),
    );
    setEditing(true);
  };
  const patchLine = (i, k, v) =>
    setEditItems((rs) =>
      rs.map((r, idx) =>
        idx === i
          ? {
              ...r,
              [k]: v,
              line_total:
                k === "qty" || k === "unit_cost"
                  ? Number(k === "qty" ? v : r.qty) *
                    Number(k === "unit_cost" ? v : r.unit_cost)
                  : r.line_total,
            }
          : r,
      ),
    );
  const addLine = (st, qty) =>
    setEditItems((rs) => [
      ...rs,
      {
        stock_id: st.id,
        stock_name: st.name,
        sku: st.sku || null,
        qty,
        unit: st.unit,
        unit_cost: Number(st.unit_cost || 0),
        line_total: qty * Number(st.unit_cost || 0),
        qty_received: 0,
      },
    ]);
  const removeLine = (i) => setEditItems((rs) => rs.filter((_, idx) => idx !== i));
  const cancelEdit = () => {
    setEditing(false);
    setEditItems([]);
  };
  const saveEdit = async () => {
    if (editItems.length === 0) {
      if (!window.confirm("This will leave the PO with no lines. Continue?")) return;
    }
    const bad = editItems.find(
      (r) => !(Number(r.qty) > 0) || !(Number(r.unit_cost) >= 0),
    );
    if (bad) {
      return toast.error("Every line needs qty > 0 and unit cost ≥ 0");
    }
    setEditBusy(true);
    try {
      const items = editItems.map((r) => ({
        stock_id: r.stock_id,
        stock_name: r.stock_name,
        sku: r.sku,
        qty: Number(r.qty),
        unit: r.unit,
        unit_cost: Number(r.unit_cost),
        order_contact_id: r.order_contact_id || null,
        qty_received: Number(r.qty_received || 0),
      }));
      const { data } = await api.put(`/po/${po.id}`, { items });
      setPo(data);
      setEditing(false);
      setEditItems([]);
      toast.success(`Draft ${po.po_number} updated`);
      window.dispatchEvent(new Event("stock-updated"));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes");
    } finally {
      setEditBusy(false);
    }
  };

  const runSubAction = async (action, opts = {}) => {
    if (!po?.id || subBusy) return;
    if (opts.confirm && !window.confirm(opts.confirm)) return;
    setSubBusy(true);
    try {
      const { data } = await api.post(`/po/${po.id}/subscription/${action}`);
      setPo(data);
      toast.success(opts.toast || "Subscription updated");
      window.dispatchEvent(new Event("stock-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update subscription");
    } finally {
      setSubBusy(false);
    }
  };

  const isService = !!po?.companion_of;
  const linkedId = po?.companion_id || po?.companion_of;

  const totals = useMemo(() => {
    if (!po) return { sub: 0, total: 0, lines: 0 };
    const sub = (po.items || []).reduce(
      (s, it) => s + (Number(it.line_total) || Number(it.qty) * Number(it.unit_cost) || 0),
      0,
    );
    return { sub, total: Number(po.total || sub) || sub, lines: (po.items || []).length };
  }, [po]);

  if (!open) return null;

  const status = resolveStatus(po);
  const sup = po?.supplier_snapshot || {};

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="po-detail-overlay"
    >
      <div
        className="nb-card bg-white max-w-3xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="po-detail-dialog"
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-black gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow flex items-center gap-2">
              <FileText size={12} strokeWidth={2.5} />
              {isService ? "Service request · read-only" : "Purchase order · read-only"}
            </div>
            <h2 className="font-display text-3xl leading-tight mt-1">
              {po?.po_number || (loading ? "Loading…" : "Not found")}
            </h2>
            {po && (
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span
                  className="text-[10px] font-black tracking-wider uppercase rounded px-1.5 py-0.5 border-2"
                  style={{
                    background: status.bg,
                    borderColor: status.border,
                    color: status.fg,
                  }}
                  data-testid="po-detail-status"
                >
                  {status.label}
                </span>
                {isService && (
                  <span
                    className="text-[10px] font-black tracking-wider uppercase bg-[#FFE9C2] border-2 border-[#7A5300] text-[#7A5300] rounded px-1.5 py-0.5"
                    data-testid="po-detail-service-badge"
                  >
                    Service request
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            data-testid="po-detail-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {po && (
          <div className="p-5 flex flex-col gap-4">
            {/* Metadata strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="nb-card-sm p-2">
                <div className="label-eyebrow flex items-center gap-1">
                  <Calendar size={11} strokeWidth={2.5} /> Created
                </div>
                <div className="text-sm mt-0.5">{fmtDate(po.created_at)}</div>
                <div className="text-[10px] text-[#607087]">by {po.created_by_name}</div>
              </div>
              <div className="nb-card-sm p-2">
                <div className="label-eyebrow flex items-center gap-1">
                  <Mail size={11} strokeWidth={2.5} /> Sent
                </div>
                <div className="text-sm mt-0.5">
                  {po.sent_at ? fmtDate(po.sent_at) : "—"}
                </div>
                {po.sent_to_email && (
                  <div className="text-[10px] text-[#607087] truncate" title={po.sent_to_email}>
                    → {po.sent_to_email}
                  </div>
                )}
              </div>
              <div className="nb-card-sm p-2">
                <div className="label-eyebrow">Lines</div>
                <div className="font-display text-xl">{totals.lines}</div>
              </div>
              <div className="nb-card-sm p-2 bg-[#DFEEF7] border-[#0A3A7A]">
                <div className="label-eyebrow">Total</div>
                <div className="font-display text-xl font-mono-num">{money(totals.total)}</div>
              </div>
            </div>

            {/* Supplier snapshot */}
            <div className="border-2 border-black rounded-xl p-3">
              <div className="label-eyebrow flex items-center gap-1.5 mb-2">
                <Building2 size={12} strokeWidth={2.5} />
                {isService ? "Processor" : "Supplier"}
              </div>
              <div className="font-display text-lg">{sup.name || "—"}</div>
              {sup.contact_name && (
                <div className="text-sm text-[#607087]">{sup.contact_name}</div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {sup.email && (
                  <a
                    href={`mailto:${sup.email}`}
                    className="text-xs font-bold text-[#228EBF] hover:underline inline-flex items-center gap-1"
                  >
                    <Mail size={11} strokeWidth={2.5} /> {sup.email}
                  </a>
                )}
                {sup.phone && (
                  <a
                    href={`tel:${sup.phone}`}
                    className="text-xs font-bold text-[#228EBF] hover:underline inline-flex items-center gap-1"
                  >
                    <Phone size={11} strokeWidth={2.5} /> {sup.phone}
                  </a>
                )}
              </div>
            </div>

            {/* Companion chip */}
            {linkedId && (
              <button
                type="button"
                data-testid="po-detail-companion-link"
                onClick={() => onOpenLinked?.(linkedId)}
                className="text-left nb-card-sm p-2 bg-[#FFF8E1] border-[#7A5300] hover:bg-[#FFE9C2] flex items-center gap-2"
              >
                <Wrench size={14} strokeWidth={2.5} className="text-[#7A5300]" />
                <div className="flex-1">
                  <div className="label-eyebrow text-[#7A5300]">
                    {isService ? "Linked to parent order" : "Linked cutting / toll-processing PO"}
                  </div>
                  <div className="text-sm font-bold text-[#3B2A00]">
                    Tap to open →
                  </div>
                </div>
                <ExternalLink size={12} strokeWidth={2.5} className="text-[#7A5300]" />
              </button>
            )}

            {/* Subscription control panel — head of chain gets Cancel /
                Pause; expired heads (superseded by a newer cycle) show
                Resume; cycles that were once part of a chain but aren't
                the head just show the info chip. */}
            {po.subscription_interval_days && (
              <div
                data-testid="po-detail-subscription"
                className={`border-2 rounded-xl p-3 ${
                  po.subscription_active
                    ? "bg-[#EAF5FB] border-[#0A3A7A]"
                    : "bg-[#F3F4F6] border-[#3D4A5C]"
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Repeat size={16} strokeWidth={2.5} className={po.subscription_active ? "text-[#0A3A7A]" : "text-[#3D4A5C]"} />
                  <div className="label-eyebrow">
                    {po.subscription_active ? "Recurring subscription — active" : "Subscription cycle (previous)"}
                  </div>
                </div>
                <div className="mt-1 text-sm">
                  <b>
                    {{ 7: "Weekly", 14: "Fortnightly", 30: "Monthly", 90: "Quarterly" }[po.subscription_interval_days] ||
                      `Every ${po.subscription_interval_days} days`}
                  </b>
                  {" · "}
                  Cycle <span className="font-mono-num">#{po.subscription_cycle || 1}</span>
                  {po.subscription_paused_until && (
                    <span className="ml-2 text-[#7A5300]">
                      · Next cycle paused until {po.subscription_paused_until}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#607087] mt-1">
                  {po.subscription_active
                    ? "The next cycle auto-appears in Receive as soon as this one is fully received."
                    : "This cycle already completed and spawned the next PO."}
                </div>
                {po.subscription_active && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button
                      data-testid="po-sub-pause-btn"
                      onClick={() =>
                        runSubAction("pause", {
                          toast: "Next cycle will be skipped",
                        })
                      }
                      disabled={subBusy || !!po.subscription_paused_until}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Skip the next auto-cloned cycle"
                    >
                      <PauseCircle size={12} strokeWidth={2.5} /> Pause 1 cycle
                    </button>
                    <button
                      data-testid="po-sub-cancel-btn"
                      onClick={() =>
                        runSubAction("cancel", {
                          confirm:
                            "Cancel this subscription? No further cycles will be auto-created. Previous cycles stay in history.",
                          toast: "Subscription cancelled",
                        })
                      }
                      disabled={subBusy}
                      className="nb-btn nb-btn-danger px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <XCircle size={12} strokeWidth={2.5} /> Cancel subscription
                    </button>
                  </div>
                )}
                {!po.subscription_active && po.subscription_interval_days && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button
                      data-testid="po-sub-resume-btn"
                      onClick={() =>
                        runSubAction("resume", { toast: "Subscription resumed" })
                      }
                      disabled={subBusy}
                      className="nb-btn nb-btn-primary px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Re-enable auto-cloning from this PO"
                    >
                      <PlayCircle size={12} strokeWidth={2.5} /> Resume from here
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Line items */}
            <div className="border-2 border-black rounded-xl overflow-hidden">
              <div className="grid grid-cols-12 gap-2 p-2.5 bg-[#FAFBFC] border-b-2 border-black items-center">
                <div className="col-span-5 sm:col-span-6 label-eyebrow">Item</div>
                <div className="col-span-2 text-right label-eyebrow">Qty</div>
                <div className="col-span-2 text-right label-eyebrow">Unit cost</div>
                <div className="col-span-3 sm:col-span-2 text-right label-eyebrow">
                  Line total
                </div>
                {/* v205 — Edit button, draft-only. Managers were deleting +
                    rebuilding whole POs just to fix a qty typo. */}
                {po.status === "draft" && !po.companion_of && !editing && (
                  <button
                    data-testid="po-edit-lines-btn"
                    onClick={startEdit}
                    className="col-span-12 mt-1 nb-btn bg-[#FFF3D4] border-[#7A5300] text-[#7A5300] px-3 py-1.5 text-xs font-bold flex items-center justify-center gap-1.5"
                    title="Tweak qty / unit cost before sending the draft"
                  >
                    <Pencil size={12} strokeWidth={2.5} /> Edit lines
                  </button>
                )}
              </div>
              {editing
                ? editItems.map((it, i) => (
                    <div
                      key={i}
                      data-testid={`po-edit-line-${i}`}
                      className="grid grid-cols-12 gap-2 p-2.5 border-b border-black/10 last:border-b-0 items-center text-sm bg-[#FFFDF4]"
                    >
                      <div className="col-span-12 sm:col-span-6 min-w-0">
                        <div className="font-bold truncate">{it.stock_name}</div>
                        {it.sku && (
                          <div className="text-[10px] text-[#607087] font-mono-num">
                            SKU {it.sku}
                          </div>
                        )}
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          data-testid={`po-edit-qty-${i}`}
                          className="nb-input py-1.5 text-sm text-right font-mono-num w-full"
                          value={it.qty}
                          onChange={(e) => patchLine(i, "qty", e.target.value)}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          data-testid={`po-edit-cost-${i}`}
                          className="nb-input py-1.5 text-sm text-right font-mono-num w-full"
                          value={it.unit_cost}
                          onChange={(e) => patchLine(i, "unit_cost", e.target.value)}
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-1 text-right font-mono-num font-bold text-xs sm:text-sm">
                        {money(Number(it.qty) * Number(it.unit_cost))}
                      </div>
                      <div className="col-span-1 text-right">
                        <button
                          data-testid={`po-edit-remove-${i}`}
                          onClick={() => removeLine(i)}
                          className="nb-btn nb-btn-danger p-1.5"
                          aria-label="remove line"
                          title="Remove this line"
                        >
                          <Trash2 size={12} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  ))
                : null}
              {editing && (
                <AddLinePicker
                  supplierId={po.supplier_id}
                  existingIds={editItems.map((r) => r.stock_id)}
                  onAdd={addLine}
                />
              )}
              {!editing &&
                (po.items || []).map((it, i) => (
                    <div
                      key={i}
                      data-testid={`po-detail-line-${i}`}
                      className="grid grid-cols-12 gap-2 p-2.5 border-b border-black/10 last:border-b-0 items-center text-sm"
                    >
                      <div className="col-span-5 sm:col-span-6">
                        <div className="font-bold">{it.stock_name}</div>
                        {it.sku && (
                          <div className="text-[10px] text-[#607087] font-mono-num">
                            SKU {it.sku}
                          </div>
                        )}
                        {(it.substitutions || []).map((sb, j) => (
                          <div
                            key={j}
                            data-testid={`po-detail-line-${i}-sub-${j}`}
                            className="text-[11px] text-[#0A3A7A] font-bold mt-0.5"
                          >
                            ↳ Substituted: {sb.qty} {sb.unit} {sb.stock_name}
                          </div>
                        ))}
                      </div>
                      <div className="col-span-2 text-right font-mono-num">
                        {it.qty} <span className="text-[10px] text-[#607087]">{it.unit}</span>
                      </div>
                      <div className="col-span-2 text-right font-mono-num">
                        {money(it.unit_cost)}
                      </div>
                      <div className="col-span-3 sm:col-span-2 text-right font-mono-num font-bold">
                        {money(it.line_total)}
                      </div>
                    </div>
                  ))}
              <div className="grid grid-cols-12 gap-2 p-2.5 bg-[#FAFBFC] border-t-2 border-black font-bold text-sm items-center">
                <div className="col-span-9 sm:col-span-10 text-right">Total</div>
                <div className="col-span-3 sm:col-span-2 text-right font-mono-num">
                  {editing
                    ? money(
                        editItems.reduce(
                          (s, r) => s + Number(r.qty) * Number(r.unit_cost),
                          0,
                        ),
                      )
                    : money(totals.total)}
                </div>
                {editing && (
                  <div className="col-span-12 flex justify-end gap-2 mt-2">
                    <button
                      data-testid="po-edit-cancel-btn"
                      onClick={cancelEdit}
                      disabled={editBusy}
                      className="nb-btn bg-white px-3 py-1.5 text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      data-testid="po-edit-save-btn"
                      onClick={saveEdit}
                      disabled={editBusy}
                      className="nb-btn nb-btn-primary px-4 py-1.5 text-xs flex items-center gap-1.5"
                    >
                      <Save size={12} strokeWidth={2.5} />
                      {editBusy ? "Saving…" : "Save draft"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* v180 — Closed / cancelled reason & notes callout */}
            {(po.status === "closed" || po.status === "cancelled") && po.closed_reason && (
              <div className="border-2 border-[#3D4A5C] rounded-lg p-3 bg-[#F3F4F6] text-sm">
                <div className="label-eyebrow text-[#3D4A5C] mb-1">
                  {po.status === "cancelled" ? "Cancelled" : "Closed"} · {fmtDate(po.closed_at)}
                  {po.closed_by && <span className="text-[#607087]"> · by {po.closed_by}</span>}
                </div>
                <div className="font-bold">
                  {{
                    manually_received: "Already received manually",
                    supplier_cancelled: "Supplier cancelled the order",
                    duplicate: "Duplicate — raised twice",
                    other: "Other reason",
                  }[po.closed_reason] || po.closed_reason}
                </div>
                {po.closed_notes && (
                  <div className="text-xs text-[#3D4A5C] mt-1 whitespace-pre-wrap">
                    {po.closed_notes}
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {po.notes && (
              <div className="border-2 border-black rounded-lg p-3 bg-[#FFF9EC] text-sm whitespace-pre-wrap">
                <div className="label-eyebrow mb-1">Notes on the PO</div>
                {po.notes}
              </div>
            )}
            {po.last_send_notes && (
              <div className="border-2 border-[#7A5300] rounded-lg p-3 bg-[#FFF8E1] text-sm whitespace-pre-wrap">
                <div className="label-eyebrow text-[#7A5300] mb-1">
                  Notes sent to receiver
                </div>
                {po.last_send_notes}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PODetailDialog;
